import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import type { AppShellProps } from './types';

const COLLAPSED_STORAGE_KEY = 'radiology.sidebarCollapsed';

export interface AppShellFullProps extends AppShellProps {
  /** Current location path, used for active-item matching. */
  currentPath: string;
}

/**
 * The frame every screen in the portal renders inside: collapsible sidebar with
 * the module switcher, sticky topbar, scrolling content area.
 *
 * Below `md` the sidebar becomes a temporary drawer that closes on navigation —
 * the legacy apps each solved this differently, and one of them not at all.
 */
export function AppShell({
  modules,
  activeModule,
  navGroups,
  currentPath,
  user,
  userContext,
  availableRoles,
  breadcrumbs,
  pageTitle,
  pageActions,
  onNavigate,
  onSignOut,
  onChangeRole,
  onOpenContextPicker,
  children,
}: AppShellFullProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () =>
      typeof window !== 'undefined' && window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1',
  );

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      window.localStorage.setItem(COLLAPSED_STORAGE_KEY, prev ? '0' : '1');
      return !prev;
    });
  }, []);

  // Navigating from the drawer should dismiss it; leaving it open covers the
  // page the user just asked for.
  const handleNavigate = useCallback(
    (to: string) => {
      setMobileOpen(false);
      onNavigate(to);
    },
    [onNavigate],
  );

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const width = collapsed
    ? theme.custom.layout.sidebarCollapsedWidth
    : theme.custom.layout.sidebarWidth;

  const sidebar = (inDrawer: boolean) => (
    <Sidebar
      modules={modules}
      activeModule={activeModule}
      navGroups={navGroups}
      currentPath={currentPath}
      collapsed={collapsed}
      onToggleCollapsed={toggleCollapsed}
      onNavigate={handleNavigate}
      inDrawer={inDrawer}
    />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      {isMobile ? (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: theme.custom.layout.sidebarWidth,
              bgcolor: 'background.default',
            },
          }}
        >
          {sidebar(true)}
        </Drawer>
      ) : (
        <Box
          component="aside"
          sx={{
            width,
            flexShrink: 0,
            height: '100dvh',
            position: 'sticky',
            top: 0,
            transition: theme.transitions.create('width', {
              duration: theme.transitions.duration.shorter,
            }),
          }}
        >
          {sidebar(false)}
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Topbar
          pageTitle={pageTitle}
          breadcrumbs={breadcrumbs}
          pageActions={pageActions}
          user={user}
          userContext={userContext}
          availableRoles={availableRoles}
          onNavigate={onNavigate}
          onOpenMobileNav={() => setMobileOpen(true)}
          onSignOut={onSignOut}
          onChangeRole={onChangeRole}
          onOpenContextPicker={onOpenContextPicker}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            p: { xs: 2, md: 3 },
            width: '100%',
            maxWidth: theme.custom.layout.contentMaxWidth,
            mx: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
